import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Printer, Share2, MapPin, Clock } from "lucide-react";
import { WHATSAPP_DISPLAY } from "../lib/whatsapp";

const API = process.env.REACT_APP_BACKEND_URL;

const catStyle = {
  VIP: "border-gold bg-gold/15 text-gold",
  Premium: "border-white/60 bg-white/10 text-white",
  Normal: "border-white/25 bg-white/5 text-white/70",
};

export default function Billet() {
  const { numero } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") || "";
  const [billet, setBillet] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/billet/${numero}?t=${encodeURIComponent(token)}`)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(setBillet)
      .catch(() => setError(true));
  }, [numero, token]);

  const handleShare = async () => {
    const url = window.location.href;
    const text = `Mon billet pour la Soirée VIP du 14 novembre — Factory58 (Bobigny) :`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Billet Soirée VIP", text, url });
        return;
      }
    } catch (e) {
      // fallback WhatsApp
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank", "noopener,noreferrer");
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#141414] px-6" data-testid="billet-error">
        <div className="text-center">
          <h1 className="font-display text-3xl text-white">Billet introuvable</h1>
          <p className="mt-4 text-sm text-white/50">Ce lien de billet est invalide. Contactez-nous sur WhatsApp au {WHATSAPP_DISPLAY}.</p>
          <a href="/" className="mt-8 inline-block text-xs uppercase tracking-[0.3em] text-gold">← Retour au site</a>
        </div>
      </div>
    );
  }

  if (!billet) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#141414]" data-testid="billet-loading">
        <p className="text-sm text-white/40">Chargement du billet...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#141414] px-6 py-20" data-testid="billet-page">
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #billet-print, #billet-print * { visibility: visible; }
          #billet-print { position: absolute; inset: 0; margin: 0 auto; box-shadow: none; }
        }
      `}</style>
      <div className="mx-auto max-w-md">
        <motion.div
          id="billet-print"
          data-testid="billet-card"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="grain relative overflow-hidden rounded-3xl border border-gold/50 bg-[#0a0a0a] shadow-2xl"
        >
          <div className="border-b border-dashed border-gold/40 px-8 pb-6 pt-8 text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.4em] text-gold">FK Conseil &amp; Événement</p>
            <h1 className="mt-3 font-display text-3xl tracking-tight text-white">
              BILLET <em className="italic text-gold">{billet.categorie}</em>
            </h1>
            <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-white/50">After Soirée VIP — Chez Ousman Gouzele</p>
            <span
              className={`mt-4 inline-block rounded-full border px-5 py-1.5 text-[11px] font-bold uppercase tracking-[0.25em] ${catStyle[billet.categorie] || catStyle.Normal}`}
              data-testid="billet-categorie"
            >
              {billet.categorie}
            </span>
          </div>

          <div className="space-y-3 px-8 py-6 text-sm">
            <div className="flex justify-between">
              <span className="text-white/40">Nom</span>
              <span className="font-semibold text-white" data-testid="billet-name">{billet.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/40">Personnes</span>
              <span className="text-white" data-testid="billet-personnes">{billet.personnes}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/40">N° de billet</span>
              <span className="font-display text-gold" data-testid="billet-numero">{billet.numero}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-white/40">Date</span>
              <span className="text-white">Samedi 14 novembre — jusqu'à 5h</span>
            </div>
            <div className="flex items-start justify-between">
              <span className="text-white/40">Lieu</span>
              <span className="text-right text-white">
                Factory58<br />
                <span className="text-xs text-white/50">12 rue Gutenberg, 93000 Bobigny</span>
              </span>
            </div>
          </div>

          <div className="border-t border-dashed border-gold/40 px-8 py-6 text-center">
            <img
              src={`${API}/api/billet-qr/${billet.numero}.png?t=${encodeURIComponent(token)}`}
              alt={`QR code billet ${billet.numero}`}
              data-testid="billet-qr"
              className="mx-auto h-44 w-44 rounded-xl border border-gold/30 bg-white p-2"
            />
            <p className="mt-4 text-[10px] uppercase tracking-[0.25em] text-white/40">
              Présentez ce QR code à l'entrée
            </p>
            <p className="mt-2 text-[9px] text-white/30">www.fk-evenement.fr — WhatsApp : {WHATSAPP_DISPLAY}</p>
          </div>
        </motion.div>

        <div className="mt-8 flex justify-center gap-4">
          <button
            type="button"
            onClick={handleShare}
            data-testid="billet-share-button"
            className="flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#0a0a0a] transition-transform duration-300 hover:scale-105"
          >
            <Share2 className="h-4 w-4" />
            Partager
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            data-testid="billet-print-button"
            className="flex items-center gap-2 rounded-full border border-gold/60 px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-[#0a0a0a]"
          >
            <Printer className="h-4 w-4" />
            Imprimer
          </button>
        </div>
        <p className="mt-6 text-center">
          <a href="/soiree-vip" className="text-[10px] uppercase tracking-[0.3em] text-white/40 transition-colors hover:text-gold">
            ← Retour à la soirée
          </a>
        </p>
      </div>
    </div>
  );
}
